import React from 'react';
import { Pagination } from 'antd';
import type { PaginationProps } from 'antd';
import { useTranslation } from 'react-i18next';

export interface TablePaginationBarProps {
  total: number;
  current: number;
  pageSize: number;
  onChange: (page: number, pageSize: number) => void;
  onShowSizeChange?: (current: number, size: number) => void;
  pageSizeOptions?: string[];
  showSizeChanger?: boolean;
  showQuickJumper?: boolean;
  size?: PaginationProps['size'];
  className?: string;
  // 可选：限制最大可翻页数。传入时，文本仍用 total 真实值，但 Pagination 内部上限按 maxPages * pageSize 截断。
  // 用于深度翻页保护场景（如后端 ES 查询 page * size 不能超过 10000）。
  maxPages?: number;
  // 合计条数后的红色括号提示
  totalHint?: string;
  // 替换左侧默认的「合计 N 条」：页面有自己的汇总口径时用，避免另起一行出现两个「合计」
  totalText?: React.ReactNode;
  paginationProps?: Omit<
    PaginationProps,
    | 'total'
    | 'current'
    | 'pageSize'
    | 'onChange'
    | 'onShowSizeChange'
    | 'pageSizeOptions'
    | 'showSizeChanger'
    | 'showQuickJumper'
    | 'size'
  >;
}

const normalizePositiveInt = (value: unknown, fallback: number): number => {
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  const intVal = Math.floor(num);
  return intVal > 0 ? intVal : fallback;
};

const normalizePageSizeOptions = (options: unknown, pageSize: number): string[] => {
  const baseOptions = Array.isArray(options) ? options : [];
  const normalized = baseOptions
    .map((opt) => {
      if (opt === null || opt === undefined) return null;
      const str = String(opt).trim();
      return str ? str : null;
    })
    .filter((v): v is string => v !== null);

  // Ensure current pageSize is included and no undefined leaks into antd Options (prevents undefined.toString crash)
  const merged = [...normalized, String(pageSize)];
  return Array.from(new Set(merged));
};

const TablePaginationBar: React.FC<TablePaginationBarProps> = ({
  total,
  current,
  pageSize,
  onChange,
  onShowSizeChange,
  pageSizeOptions = ['10', '20', '50', '100'],
  showSizeChanger = true,
  showQuickJumper = true,
  size = 'small',
  className,
  maxPages,
  totalHint,
  totalText,
  paginationProps,
}) => {
  const { t } = useTranslation();
  const safeTotal = Number.isFinite(Number(total)) ? Number(total) : 0;
  const safeCurrent = normalizePositiveInt(current, 1);
  const safePageSize = normalizePositiveInt(pageSize, 10);
  const safePageSizeOptions = normalizePageSizeOptions(pageSizeOptions, safePageSize);
  // 深度翻页保护：当传入 maxPages 时，Pagination 的 total 用截断后的值，确保最多只能翻到 maxPages
  const paginationTotal = (typeof maxPages === 'number' && maxPages > 0)
    ? Math.min(safeTotal, maxPages * safePageSize)
    : safeTotal;
  // 总数超过深度翻页上限时，文本切换为带"只显示 10000 条"提示的版本（10000 写死在 i18n 里）
  const isCapped = typeof maxPages === 'number' && maxPages > 0 && safeTotal > maxPages * safePageSize;
  const totalLabel = isCapped
    ? t('table.pagination.totalItemsCapped', { total: safeTotal })
    : t('table.pagination.totalItems', { total: safeTotal });

  return (
    <div className={`flex justify-between items-center ${className || ''}`.trim()}>
      <div className="text-gray-500">
        {totalText ?? (totalHint ? (
          <>
            {t('table.pagination.totalItems', { total: safeTotal })}
            <span className="text-red-500">（{totalHint}）</span>
          </>
        ) : (
            totalLabel
        ))}
      </div>
      <Pagination
        {...paginationProps}
        current={safeCurrent}
        total={paginationTotal}
        pageSize={safePageSize}
        size={size}
        showSizeChanger={showSizeChanger}
        showQuickJumper={showQuickJumper}
        pageSizeOptions={safePageSizeOptions}
        onChange={onChange}
        onShowSizeChange={onShowSizeChange}
      />
    </div>
  );
};

export default TablePaginationBar;

